import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Button as MatButton,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../../../api/axios";
import moment from "moment";
import DataLoader from "../../../../shared/DataLoader/DataLoader";
import DataNotFound from "../../../../shared/DataNotFound/DataNotFound";
import HandleInterviewLink, { handleDate } from "../../../../shared/CommonFunctions/CommonFunction";

interface UpcomingInterviewsListingProps {
  data?: any[];
  pageName?: string;
  dateRange?:any;
}

const UpcomingInterviewsListing: React.FC<UpcomingInterviewsListingProps> = ({
  data,
  pageName,
  dateRange
}) => {
  const navigate = useNavigate();
  const [interViewData, setInterViewData] = useState<any>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [page, setPage] = useState(0);
  const [offset, setOffset] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortOrder, setSortOrder] = useState<any>("asc");
  const [sortBy, setSortBy] = useState("position_name");

  const formatTime = (startTime: any, endTime: any) => {
    if (startTime === "NA") return startTime;
    return `${moment(startTime, "HH:mm:ss").format("h:mm A")} To ${moment(
      endTime,
      "HH:mm:ss"
    ).format("h:mm A")}`;
  };

  const getInterviewData = async () => {
    try {
      setLoading(true);
      let response = await API.post(`/interview/candidate/my/calendar`, {
        offset: offset,
        limit: rowsPerPage,
        view: "list",
        sort: sortBy,
        start_date:dateRange?.startDate,
        end_date:dateRange?.endDate,
      });
      setInterViewData(response?.data?.data);
      setTotalRecords(response?.data?.meta?.filter_count);
    } catch (error) {
      // Failed to fetch upcoming interviews
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePageChange = (event: any, newPage: any) => {
    setPage(newPage);
    setOffset(newPage * rowsPerPage);
  };

  const handleSort = (property: any) => {
    const isAsc = sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(sortOrder === "asc" ? `-${property}` : property);
  };

  useEffect(() => {
    getInterviewData();
  }, [offset, rowsPerPage, sortBy, dateRange]);

  useEffect(() => {
    setOffset(0)
    setPage(0)
  }, [dateRange]);

  return (
    <>
      <Box marginTop={1} sx={{ overflowY: "auto", flex: "1 1 auto", display: "grid" }}>
        <TableContainer className="secondary__table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                <TableSortLabel
                    active={sortBy === "position_name"}
                    direction={sortOrder}
                    onClick={() => handleSort("position_name")}
                  >
                   Position Title
                  </TableSortLabel>
                  </TableCell>
                <TableCell>
                <TableSortLabel
                    active={sortBy === "slot_date"}
                    direction={sortOrder}
                    onClick={() => handleSort("slot_date")}
                  >
                   Date
                  </TableSortLabel>
                  </TableCell>
                <TableCell>Time</TableCell>
                <TableCell align="left">Interview Link</TableCell>
                {pageName !== "dashboard" && (
                  <TableCell align="center">Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableCell colSpan={5} align="center">
                  <Box sx={{ height: "300px" }}>
                    <DataLoader />
                  </Box>
                </TableCell>
              ) : interViewData?.length > 0 ? (
                interViewData.map((interviewer: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Box sx={{ padding: "14px 0px" }}>
                        {interviewer?.position_name || "NA"}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ padding: "14px 0px" }}>
                        {handleDate(interviewer?.slot_date)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ padding: "14px 0px" }}>
                        {formatTime(
                          interviewer?.start_time || "NA",
                          interviewer?.end_time || "NA"
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="left">
                    <HandleInterviewLink link={interviewer?.webex_link}/>
                    </TableCell>
                    {pageName !== "dashboard" && (
                      <TableCell align="center">
                        <MatButton
                          variant="text"
                          sx={{ padding: 0 }}
                          onClick={() =>
                            navigate("view", {
                              state: {
                                view: "list",
                                calenderData: { ...interviewer },
                              },
                            })
                          }
                        >
                          View
                        </MatButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Box
                      sx={{
                        maxHeight: "300px",
                      }}
                    >
                      <DataNotFound />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {interViewData?.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Box>

    </>
  );
};

export default UpcomingInterviewsListing;
